import { faCircle } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { RequestLifeCycle } from 'app/appModels';
import { MprcButton, MprcCard, MprcContainer, MprcIconFa, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { ButtonAdd } from 'components/buttons';
import { IconButtonClose, IconButtonDelete } from 'components/iconButtons';
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcTimePicker } from "components/MprcTimePicker";
import { CreateOrUpdatePollThreadCommand, GetPollThreadPublicationDto, PollItemModel, PublicationApprovalStatus, PublicationType } from 'generated/CoreApi_v1';
import { useReferredState } from "hooks/useReferredState";
import LibUtils from 'lib/LibUtils';
import moment from 'moment';
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NIL, v4 as uuidv4 } from 'uuid';
import { ICreatePublicationProps } from "views/knowledge/publication/creation";
import { IPublicationChannelBoxValidation } from './components';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IValidation {
    title?: boolean;
    answers?: boolean;
    dateUntil?: boolean;
    timeUntil?: boolean;
}

interface IState {
    record: GetPollThreadPublicationDto;
    validation: IValidation & IPublicationChannelBoxValidation;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const CreatePollThread = (props: ICreatePublicationProps) => {

    const { onClose } = props;

    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();
    const refTitle = useRef<HTMLTextAreaElement | undefined>(undefined);

    const defaultAnswer = (id: string) => ({ id } as PollItemModel);
    const minAnswers = 2;
    const maxAnswers = 7;

    let timerLastChange: number;
    let timer: ReturnType<typeof setTimeout>;

    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [lastError, setLastError] = useState<any>();
    const [dateUntil, setDateUntil] = useState<moment.Moment | null>(null);
    const [timeUntil, setTimeUntil] = useState<moment.Moment | null>(null);
    const [loadDto, setLoadDto] = useState<GetPollThreadPublicationDto>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);

    const [state, setState, refState] = useReferredState<IState>(
        {
            record: {
                publicationId: NIL,
                content: "",
                title: "",
                attachs: [],
                anonymousAnswers: true,
                publicationType: PublicationType.Poll,
                answers: [defaultAnswer(uuidv4()), defaultAnswer(uuidv4())],
                allowAnswersUntil: null,
                hasAnswers: false,
                threadId: props.parent?.parentId ?? NIL,
                presentation: null
            },
            validation: {
                title: undefined,
                answers: undefined,
                channel: undefined,
                dateUntil: undefined,
                timeUntil: undefined
            }
        });

    const autoFocusIndex = state.record.title == "" ? 0 : state.record.answers.findIndex(p => p.text?.length == 0);
    const readOnly = state.record.hasAnswers;
    const endOfDay = state.record.allowAnswersUntil != null
        && state.record.allowAnswersUntil.getHours() == 23
        && state.record.allowAnswersUntil.getMinutes() == 59
        && state.record.allowAnswersUntil.getSeconds() == 59;

    const steps = [];
    const StepFirstPage = 1;

    ///
    const updateRecord = (data: Partial<GetPollThreadPublicationDto>) => {
        return { ...refState.current.record, ...data };
    };

    ///
    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const value = event.target.value;
        setState({
            ...state,
            record: updateRecord({ title: value }),
            validation: { ...state.validation, title: value.length > 0 }
        });
    };

    ///
    const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, index: number) => {
        const value = event.target.value;

        const newAnswers = state.record.answers.map((v, i) => {
            if (i == index) {
                v.text = value;
            }
            return v;
        });

        setState({
            ...state,
            record: updateRecord({ answers: newAnswers }),
            validation: { ...state.validation, answers: state.record.answers.every(x => x.text?.length > 0) }
        });
    };

    ///
    const handleTitleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {

        if (timer) clearTimeout(timer);

        if (!refTitle.current) return;
        timerLastChange = new Date().getTime();

        timer = setTimeout(() => {
            if ((new Date().getTime() - timerLastChange) > 200) {
                //  Adiciona "?" no final do texto.
                if (refTitle.current!.value.length > 0 && refTitle.current!.value[refTitle.current!.value.length - 1] != "?") {
                    refTitle.current!.value += "?";
                    setState({
                        ...state,
                        record: updateRecord({ title: refTitle.current!.value }),
                    });
                    handleTitleCaret();
                }
            }
        }, 200);
    };

    ///
    const handleTitleCaret = () => {
        // Reposiciona CARET antes do "?" do final do texto.
        if (refTitle.current) {
            if (refTitle.current.selectionStart == refTitle.current.selectionEnd) { // Garante que não está selecionando um texto
                if (refTitle.current.value.length > 1 && (refTitle.current.selectionEnd ?? 0) > refTitle.current.value.length - 1) {
                    refTitle.current.selectionStart = refTitle.current.selectionEnd = refTitle.current.value.length - 1;
                }
            }
        }
    };

    ///
    const handleAddOption = () => {

        if (state.record.answers.length < maxAnswers) {
            const currentAnswers = state.record.answers ?? [];
            const newAnswers = [...currentAnswers, defaultAnswer(uuidv4())];

            setState({
                ...state,
                record: updateRecord({ answers: newAnswers }),
                validation: { ...state.validation, answers: newAnswers.every(x => x.text?.length > 0) }
            });
        }
    };

    ///
    const handleDelete = (index: number) => {

        if (state.record.answers.length > minAnswers) {
            const newAnswers = state.record.answers;
            newAnswers.splice(index, 1);

            setState({
                ...state,
                record: updateRecord({ answers: newAnswers }),
                validation: { ...state.validation, answers: newAnswers.every(x => x.text?.length > 0) }
            });
        }
    };

    ///
    const canSave = (): boolean => {
        const { validation } = state;

        return validation.answers === true
            && (validation.dateUntil === true || validation.dateUntil == undefined)
            && (validation.timeUntil === true || validation.timeUntil == undefined)
            && recordChanged;
    };

    ///
    const save = () => {
        const { record } = state;

        let allowAnswersUntil = null;
        if (dateUntil != null) {
            allowAnswersUntil = dateUntil.toDate();
            allowAnswersUntil.setHours(timeUntil == null ? 23 : timeUntil.hours());
            allowAnswersUntil.setMinutes(timeUntil == null ? 59 : timeUntil.minutes());
            allowAnswersUntil.setSeconds(timeUntil == null ? 59 : timeUntil.seconds());
        }

        const dto: CreateOrUpdatePollThreadCommand = {
            publicationId: record.publicationId,
            title: record.title,
            content: record.content,
            anonymousAnswers: record.anonymousAnswers,
            allowAnswersUntil,
            answers: record.answers,
            threadId: record.threadId,
            contentUnformatted: null,
        };

        setFormCycle(RequestLifeCycle.Posting);

        api.v1.knowledgePublication.createOrUpdatePollThreadPublication(dto)
            .then((response) => {
                onClose(response.data);
                setFormCycle(RequestLifeCycle.None);

                if (response.data.approvalStatus == PublicationApprovalStatus.Approved)
                    enqueueSnackbar(response.data.newPublicationId ? t("Publication.CreatePoll.PublishMessage") : t("Publication.CreatePoll.PublishUpdateMessage"), { variant: "success" });
                else
                    enqueueSnackbar(response.data.newPublicationId ? t("Publication.CreatePoll.PublishModerationMessage") : t("Publication.CreatePoll.PublishModerationUpdateMessage"), { variant: "warning" });
            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///
    const load = async (publicationId: string) => {
        setFormCycle(RequestLifeCycle.Loading);
        try {
            const result = await api.v1.knowledgePublication.getPollThreadPublication({ publicationId });
            const record = result.data;

            setState({
                record,
                validation: {
                    answers: true,
                    channel: true,
                    dateUntil: true,
                    timeUntil: true
                }
            });
            setLoadDto(LibUtils.deepCopy(record));
            setDateUntil(record.allowAnswersUntil == null ? null : moment(record.allowAnswersUntil));
            setTimeUntil(record.allowAnswersUntil == null ? null : moment(record.allowAnswersUntil));
            setFormCycle(RequestLifeCycle.None);
        }
        catch (error: any) {
            setLastError(error);
            setFormCycle(RequestLifeCycle.LoadFailed);
        }
    };

    ///
    steps[StepFirstPage] = () => {
        const { record, validation } = state;

        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column" }}>

                    <MprcCard paperColor={["paper", "dark"]} sx={{ p: 2, mb: 2 }}>
                        {props.parent?.parentTitle ?? record.title ?? "-"}
                    </MprcCard>

                    <MprcContainer>
                        <TextField
                            autoFocus
                            fullWidth
                            name="title"
                            margin="normal"
                            multiline={true}
                            placeholder={t("Publication.CreatePoll.TitlePlaceholder")}
                            autoComplete="off"
                            autoCorrect="off"
                            tabIndex={0}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 255 },
                                    style: { fontSize: 18 },
                                    readOnly
                                }
                            }}
                            value={record.title}
                            inputRef={refTitle}
                            onChange={e => handleTitleChange(e)}
                            onKeyUp={handleTitleKeyUp}
                            error={validation.title === false}
                        />
                    </MprcContainer>

                    <MprcContainer>
                        <Grid container spacing={1} size={{ xs: 12 }}>
                            <Grid size="grow">
                                {record.answers.map((v, index) =>
                                    <Grid key={index} container alignContent="center" alignItems="center" spacing={1}>
                                        <Grid>
                                            <MprcIconFa icon={faCircle} />
                                        </Grid>
                                        <Grid size="grow">
                                            <TextField
                                                id={record.answers[index].id}
                                                fullWidth
                                                autoFocus={index == autoFocusIndex}
                                                autoComplete="off"
                                                placeholder={t("Publication.CreatePoll.OptionPlaceholder")}
                                                error={record.answers[index].text != undefined && validation.answers === false && record.answers[index].text?.length == 0}
                                                value={record.answers[index].text}
                                                disabled={readOnly}
                                                onChange={e => handleAnswerChange(e, index)}
                                                onKeyDown={
                                                    (e) => {
                                                        if (e.keyCode == 13) {
                                                            if (index < record.answers.length - 1) {
                                                                const el = document.getElementById(record.answers[index + 1].id);
                                                                if (el) el.focus();
                                                            }
                                                            else if (record.answers[index].text?.length > 0 && record.answers.length < maxAnswers) {
                                                                handleAddOption();
                                                            }
                                                        }
                                                    }
                                                }
                                            />
                                        </Grid>
                                        {
                                            record.answers.length > minAnswers && !readOnly &&
                                            <Grid>
                                                <IconButtonDelete
                                                    size="small"
                                                    onClick={() => handleDelete(index)}
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </MprcContainer>
                    <MprcContainer>
                        {record.answers.length < maxAnswers && !readOnly &&
                            <ButtonAdd
                                sx={{ pl: 0 }} text={t("Publication.CreatePoll.AddOptionButtonText")}
                                onClick={handleAddOption}
                            />
                        }
                    </MprcContainer>
                    <MprcContainer>
                        <Grid container justifyContent={"space-between"} alignItems="center" direction="row">
                            <Grid size="grow">
                                <Grid container spacing={2}>
                                    <Grid>
                                        <MprcDatePicker
                                            error={validation.dateUntil === false}
                                            label={t("Publication.CreatePoll.DateAllowAnswersUntilLabel")}
                                            value={dateUntil?.toDate() ?? null}
                                            onError={(reason, v) => setState({ ...state, validation: { ...validation, dateUntil: reason == undefined } })}
                                            onChange={(date, v) => setDateUntil(moment(date))}
                                            minDate={moment().add(1, 'days').toDate()}
                                        />
                                    </Grid>
                                    {dateUntil &&
                                        <Grid size={{ xs: 3 }}>
                                            <MprcTimePicker
                                                error={validation.timeUntil === false}
                                                label={t("Publication.CreatePoll.TimeAllowAnswersUntilLabel")}
                                                value={endOfDay ? null : timeUntil?.toDate() ?? null}
                                                onError={(reason, v) => setState({ ...state, validation: { ...validation, timeUntil: reason == undefined } })}
                                                onChange={(date, v) => setTimeUntil(moment(date))}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                            <Grid>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={record.anonymousAnswers}
                                            onChange={() => setState({ ...state, record: updateRecord({ anonymousAnswers: !record.anonymousAnswers }) })}
                                        />}
                                    label={t("Publication.CreatePoll.IsAnonymousLabel")}
                                />
                            </Grid>
                        </Grid>
                    </MprcContainer>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" width="100%">
                        <Grid>
                        </Grid>
                        <Grid>
                            <MprcButton
                                variant="contained"
                                color="success"
                                onClick={() => save()}
                                disabled={!canSave()}
                            >
                                <span>{t("Publication.Common.Form.ButtonPublishSequenceText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (props.publicationId != null) {
            load(props.publicationId);
        }
    }, []);

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(state.record, loadDto);
        setRecordChanged(isRecordChanged);
    }, [state]);

    ///////////////////////////////////////////////////////////////////////
    /// RENDER
    ///////////////////////////////////////////////////////////////////////
    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {t("Publication.CreatePoll.TitleNewSequence")}
                        </Typography>
                        {state.record.hasAnswers && <Typography variant="bodyNotice">{t("Publication.CreatePoll.PeopleAlreadyVotedMessage")}</Typography>}
                    </Grid>
                    <Grid>
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(null)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />
            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />
                            {steps[1]()}
                        </>
                    )
            }

        </Dialog>
    );
};
